import React, { useState, useEffect } from 'react';
import { FileText, ChevronRight, CreditCard, Smartphone, Apple, Coins, CheckCircle, User, Loader, Banknote, Clock, Check, Landmark, DollarSign, X } from 'lucide-react';
import { CustomerSelector } from './appointments/CustomerSelector';
import { motion, AnimatePresence } from 'framer-motion';
import { chargeCustomer } from '../lib/chargeCustomer';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/auth/hooks';
import { supabase } from '../lib/supabase';
import { BottomSheet } from './ui/BottomSheet';

const paymentMethods = [
  {
    key: 'credit_card',
    label: 'כרטיס אשראי',
    icon: <CreditCard className="h-8 w-8 text-blue-600" />,
    bgColor: 'from-blue-50 to-blue-100',
    borderColor: 'border-blue-200',
    selectedBg: 'bg-blue-100',
    selectedBorder: 'border-blue-500'
  },
  {
    key: 'bit',
    label: 'BIT',
    icon: <Smartphone className="h-8 w-8 text-orange-500" />,
    bgColor: 'from-orange-50 to-orange-100',
    borderColor: 'border-orange-200',
    selectedBg: 'bg-orange-100',
    selectedBorder: 'border-orange-500'
  },
  {
    key: 'cash',
    label: 'מזומן',
    icon: <Coins className="h-8 w-8 text-green-600" />,
    bgColor: 'from-green-50 to-green-100',
    borderColor: 'border-green-200',
    selectedBg: 'bg-green-100',
    selectedBorder: 'border-green-500'
  },
  {
    key: 'bank_transfer',
    label: 'העברה בנקאית',
    icon: <Landmark className="h-8 w-8 text-indigo-600" />,
    bgColor: 'from-indigo-50 to-indigo-100',
    borderColor: 'border-indigo-200',
    selectedBg: 'bg-indigo-100',
    selectedBorder: 'border-indigo-500'
  },
  {
    key: 'other',
    label: 'אחר',
    icon: <DollarSign className="h-8 w-8 text-gray-500" />,
    bgColor: 'from-gray-50 to-gray-100',
    borderColor: 'border-gray-200',
    selectedBg: 'bg-gray-100',
    selectedBorder: 'border-gray-400'
  },
];

const tipOptions = [5, 10, 15];

// הוסף שלב חדש: בחירת סוג חיוב (תור או חיוב כללי)
const chargeTypes = [
  { key: 'appointment', label: 'חיוב על תור קיים' },
  { key: 'product', label: 'חיוב על מוצר' },
  { key: 'manual', label: 'חיוב כללי (ללא תור)' }
];

export default function ChargeCustomerFlow({
  onClose,
  businessId: propBusinessId,
  defaultCustomer,
  appointmentId,
  skipCustomerStep,
  skipChargeTypeStep,
  defaultChargeType,
  defaultAppointment,
  onSuccess,
  open = true,
  disableBackOnStep,
}: {
  onClose: () => void;
  businessId?: string;
  defaultCustomer?: {
    customerId: string;
    customerName: string;
    customerPhone: string;
    customerEmail?: string;
  };
  appointmentId?: string;
  skipCustomerStep?: boolean;
  skipChargeTypeStep?: boolean;
  defaultChargeType?: 'appointment' | 'manual' | 'product';
  defaultAppointment?: any;
  onSuccess?: () => void;
  open?: boolean;
  disableBackOnStep?: number;
}) {
  const { business, user } = useAuth();
  const [businessId, setBusinessId] = useState<string>('');
  const [step, setStep] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    paymentMethod: '',
    tipPercent: null as null | number,
    tipAmount: '',
    amount: '',
    installments: '1',
  });

  const [chargeType, setChargeType] = useState<'appointment' | 'manual' | 'product' | ''>('');
  const [appointments, setAppointments] = useState<any[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<any | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [productQuantity, setProductQuantity] = useState<number>(1);
  const [terminalsLoaded, setTerminalsLoaded] = useState(false);

  const [loadingMetadata, setLoadingMetadata] = useState(false);

  const [showAddCustomerPopup, setShowAddCustomerPopup] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({ name: '', phone: '', email: '' });

  const [terminals, setTerminals] = useState<any[]>([]);
  const [selectedTerminal, setSelectedTerminal] = useState<any | null>(null);

  // שמור את השלב הראשון שבו התחלנו
  const [initialStep, setInitialStep] = useState<number | null>(null);

  // הוסף useEffect ששולף את הקישור לחשבונית מהטבלה payment_transactions לפי appointmentId (אם קיים)
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);

  useEffect(() => {
    async function fetchInvoiceUrl() {
      if (!appointmentId) {
        setInvoiceUrl(null);
        return;
      }
      const { data, error } = await supabase
        .from('payment_transactions')
        .select('invoice_link')
        .eq('appointment_id', appointmentId)
        .eq('status', 'success')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!error && data?.invoice_link) {
        setInvoiceUrl(data.invoice_link);
      } else {
        setInvoiceUrl(null);
      }
    }
    fetchInvoiceUrl();
  }, [appointmentId]);

  // שליפת מזהה העסק
  useEffect(() => {
    async function fetchBusinessIdFromMetadata() {
      // if (!open) return;
      setLoadingMetadata(true);
      try {
        if (propBusinessId && propBusinessId.trim() !== '') {
          setBusinessId(propBusinessId);
          setError(null);
          return;
        }
        if (business?.id) {
          setBusinessId(business.id);
          setError(null);
          return;
        }
        if (user) {
          const { data: metadata, error: metadataError } = await supabase
            .from('users')
            .select('business_id')
            .eq('id', user.id)
            .single();
          if (metadataError) throw new Error('לא ניתן לאתר את מזהה העסק');
          if (metadata && metadata.business_id) {
            setBusinessId(metadata.business_id);
            setError(null);
            return;
          }
        }
        throw new Error('לא ניתן לאתר את מזהה העסק');
      } catch (error: any) {
        setError(error.message || 'אירעה שגיאה בהשגת מזהה העסק');
      } finally {
        setLoadingMetadata(false);
      }
    }
    fetchBusinessIdFromMetadata();
  }, [propBusinessId, business, user]);

  // טען תורים של הלקוח אם נבחר חיוב על תור
  useEffect(() => {
    if (chargeType === 'appointment' && formData.customerId) {
      supabase
        .from('appointments')
        .select('id, start_time, services(name_he, price), status, staff:users!staff_id(name)')
        .eq('customer_id', formData.customerId)
        .in('status', ['booked', 'confirmed'])
        .order('start_time', { ascending: false })
        .then(({ data }) => setAppointments(data || []));
    }
  }, [chargeType, formData.customerId]);

  // טען מוצרים של העסק אם נבחר חיוב על מוצר
  useEffect(() => {
    if (chargeType === 'product' && businessId) {
      supabase
        .from('products')
        .select('id, name, price')
        .eq('business_id', businessId)
        .order('name', { ascending: true })
        .then(({ data }) => setProducts(data || []));
    }
  }, [chargeType, businessId]);

  // טען מסופי תשלום פעילים של העסק
  useEffect(() => {
    if (businessId && open) {
      setTerminalsLoaded(false);
      supabase
        .from('payment_terminals')
        .select(`
          *,
          terminal_request:terminal_requests(form_data)
        `)
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .then(({ data }) => {
          setTerminals(data || []);
          if (data && data.length > 0) {
            setSelectedTerminal(data[0]);
          }
          setTerminalsLoaded(true);
        });
    }
  }, [businessId, open]);

  // איפוס הטופס כשסוגרים את החלון
  useEffect(() => {
    if (!open) {
      setStep(1);
      setSelectedCustomer(null);
      setError(null);
      setFormData({
        customerId: '',
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        paymentMethod: '',
        tipPercent: null,
        tipAmount: '',
        amount: '',
        installments: '1',
      });
    }
  }, [open]);

  const didInitRef = React.useRef(false);

  useEffect(() => {
    if (open) didInitRef.current = false;
  }, [open]);

  // אוטומציה של שלבים 1 ו־2 אם יש פרופס מתאימים
  useEffect(() => {
    if (!open || didInitRef.current || !terminalsLoaded) return;

    if (skipCustomerStep && defaultCustomer) {
      setFormData(f => ({
        ...f,
        customerId: defaultCustomer.customerId || '',
        customerName: defaultCustomer.customerName || '',
        customerPhone: defaultCustomer.customerPhone || '',
        customerEmail: defaultCustomer.customerEmail || '',
      }));
      setSelectedCustomer({
        customerId: defaultCustomer.customerId || '',
        customerName: defaultCustomer.customerName || '',
        customerPhone: defaultCustomer.customerPhone || '',
        customerEmail: defaultCustomer.customerEmail || '',
      });
      const nextStep = skipChargeTypeStep ? (defaultChargeType === 'appointment' ? 2.5 : defaultChargeType === 'product' ? 2.6 : terminals.length > 0 ? 2.8 : 3) : 2;
      setStep(nextStep);
      setInitialStep(nextStep); // שמור את השלב הראשון
    }

    if (skipChargeTypeStep && defaultChargeType) {
      setChargeType(defaultChargeType);
      const nextStep = defaultChargeType === 'appointment' ? 2.5 : defaultChargeType === 'product' ? 2.6 : terminals.length > 0 ? 2.8 : 3;
      setStep(nextStep);
      setInitialStep(nextStep); // שמור את השלב הראשון
    }

    if (defaultAppointment && defaultChargeType === 'appointment') {
      setSelectedAppointment(defaultAppointment);
      const price =
        typeof defaultAppointment.metadata?.price === 'number'
          ? defaultAppointment.metadata.price
          : typeof defaultAppointment.services?.price === 'number'
          ? defaultAppointment.services.price
          : typeof defaultAppointment.price === 'number'
          ? defaultAppointment.price
          : '';
      setFormData(f => ({
        ...f,
        amount: price ? price.toString() : '',
      }));
      const nextStep = terminals.length > 0 ? 2.8 : 3;
      setStep(nextStep);
      setInitialStep(nextStep); // שמור את השלב הראשון
    }

    didInitRef.current = true;
  }, [open, skipCustomerStep, skipChargeTypeStep, defaultCustomer, defaultChargeType, defaultAppointment, terminalsLoaded, terminals]);

  useEffect(() => {
    if (!defaultAppointment || defaultChargeType !== 'appointment') return;
    if (didInitRef.current || !terminalsLoaded) return;

    const price =
      typeof defaultAppointment.metadata?.price === 'number'
        ? defaultAppointment.metadata.price
        : typeof defaultAppointment.services?.price === 'number'
        ? defaultAppointment.services.price
        : typeof defaultAppointment.price === 'number'
        ? defaultAppointment.price
        : '';
    setFormData(f => ({
      ...f,
      customerId: defaultAppointment.customers?.id || defaultAppointment.customer_id || '',
      customerName: defaultAppointment.customers?.name || defaultAppointment.customer_name || '',
      customerPhone: defaultAppointment.customers?.phone || defaultAppointment.customer_phone || '',
      customerEmail: defaultAppointment.customers?.email || '',
      amount: price ? price.toString() : '',
    }));
    setSelectedCustomer({
      customerId: defaultAppointment.customers?.id || defaultAppointment.customer_id || '',
      customerName: defaultAppointment.customers?.name || defaultAppointment.customer_name || '',
      customerPhone: defaultAppointment.customers?.phone || defaultAppointment.customer_phone || '',
      customerEmail: defaultAppointment.customers?.email || '',
    });
    setSelectedAppointment(defaultAppointment);
    setChargeType('appointment');
    setStep(2);
  }, [defaultAppointment, defaultChargeType, terminals, terminalsLoaded]);

  const prevOpenRef = React.useRef(open);
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      setStep(1);
      setFormData({
        customerId: '',
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        paymentMethod: '',
        tipPercent: null,
        tipAmount: '',
        amount: '',
        installments: '1',
      });
      didInitRef.current = false;
    }
    prevOpenRef.current = open;
  }, [open]);

  // אם אתה פותח את ChargeCustomerFlow בלי הפרופ open, הוא לא יוצג בכלל (כי יש תנאי if (!open) return null;).

  if (loadingMetadata) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-2xl shadow-lg p-4 max-w-md w-full">
          <div className="flex flex-col items-center justify-center py-4">
            <Loader className="h-8 w-8 text-indigo-600 animate-spin mb-3" />
            <p className="text-gray-700">טוען נתוני עסק...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !businessId || businessId.trim() === '') {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-2xl shadow-lg p-4 max-w-md w-full">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-medium text-red-600">שגיאה</h3>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-gray-700">{error || 'מזהה העסק חסר. לא ניתן להמשיך בתהליך החיוב.'}</p>
          <p className="text-gray-500 text-sm mt-1">אנא רענן את הדף או התחבר מחדש למערכת.</p>
          <button 
            className="w-full mt-4 bg-indigo-600 text-white rounded-xl py-2 font-medium"
            onClick={onClose}
          >
            סגור
          </button>
        </div>
      </div>
    );
  }

  // Helper function to get terminal info for header
  const getTerminalInfo = () => {
    if (!selectedTerminal) return null;
    return {
      name: selectedTerminal.terminal_name || 'מסוף ללא שם',
      regId: selectedTerminal.terminal_request?.form_data?.regId || 'לא זמין'
    };
  };

  function StepIndicator({ step }: { step: number }) {
    const getCurrentStep = () => {
      if (step === 1) return 1;
      if (step === 2 || step === 2.5 || step === 2.6 || step === 2.7 || step === 2.8) return 2;
      if (step === 3) return 3;
      if (step === 4) return 4;
      if (step === 5) return 5;
      if (step === 6) return 6;
      return step;
    };

    const currentStep = getCurrentStep();
    const totalSteps = 6;
    const progressPercentage = (currentStep / totalSteps) * 100;

    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-sm font-medium text-gray-600">שלב {currentStep} מתוך {totalSteps}</span>
          <span className="text-xs text-gray-500">{Math.round(progressPercentage)}%</span>
        </div>
        <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            key={currentStep}
            className="absolute top-0 right-0 h-full bg-gradient-to-l from-indigo-600 to-indigo-400 rounded-full"
            initial={{ width: `${progressPercentage}%` }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          />
        </div>
      </div>
    );
  }

  // Step 1: Customer selection
  if (step === 1 && !skipCustomerStep) {
    return (
      <PopupCard title="בחירת לקוח לחיוב" onClose={onClose}>
        <StepIndicator step={step} />
        <div className="mb-2 text-center font-semibold text-indigo-700 text-base">בחר לקוח לפי שם או מספר טלפון</div>
        <div className="flex flex-col gap-3 w-full items-center">
          <CustomerSelector
            formData={formData}
            onChange={data => setFormData({ ...formData, ...data })}
            onSubmit={() => {
              setSelectedCustomer(formData);
              setStep(2);
            }}
            businessId={businessId}
            enablePhoneSearch={true}
            submitLabel={null}
            submitButtonProps={{ style: { display: 'none' } }}
          />
          <button
            className="w-full max-w-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-4 py-2 font-medium transition shadow"
            style={{ fontSize: '1rem', letterSpacing: '0.02em' }}
            onClick={() => setShowAddCustomerPopup(true)}
            type="button"
          >
            לקוח חדש
          </button>
        </div>
        {/* פופאפ להוספת לקוח חדש */}
        {showAddCustomerPopup && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-xs mx-auto border border-gray-200">
              <h3 className="text-lg font-bold text-indigo-700 mb-3 text-center">הוספת לקוח חדש</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="שם מלא"
                  value={newCustomerData.name}
                  onChange={e => setNewCustomerData(d => ({ ...d, name: e.target.value }))}
                />
                <input
                  type="tel"
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="מספר טלפון"
                  value={newCustomerData.phone}
                  onChange={e => setNewCustomerData(d => ({ ...d, phone: e.target.value }))}
                />
                <input
                  type="email"
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="אימייל (אופציונלי)"
                  value={newCustomerData.email}
                  onChange={e => setNewCustomerData(d => ({ ...d, email: e.target.value }))}
                />
              </div>
              <div className="flex gap-2 mt-5">
                <button
                  className="flex-1 bg-gray-100 text-gray-600 rounded-xl py-2 font-medium"
                  onClick={() => setShowAddCustomerPopup(false)}
                  type="button"
                >
                  ביטול
                </button>
                <button
                  className="flex-1 bg-indigo-600 text-white rounded-xl py-2 font-medium hover:bg-indigo-700"
                  type="button"
                  onClick={async () => {
                    if (!newCustomerData.name || !newCustomerData.phone) {
                      toast.error('נא למלא שם ומספר טלפון');
                      return;
                    }
                    // בדוק אם המספר טלפון כבר קיים
                    const { data: existing, error: existError } = await supabase
                      .from('customers')
                      .select('id')
                      .eq('business_id', businessId)
                      .eq('phone', newCustomerData.phone)
                      .maybeSingle();
                    if (existing) {
                      toast.error('מספר טלפון זה כבר קיים במערכת');
                      return;
                    }
                    // המשך להוספה רגילה
                    const { data, error } = await supabase
                      .from('customers')
                      .insert([{
                        business_id: businessId,
                        name: newCustomerData.name,
                        phone: newCustomerData.phone,
                        email: newCustomerData.email || '',
                      }])
                      .select()
                      .single();
                    if (error) {
                      toast.error('שגיאה ביצירת לקוח חדש');
                      return;
                    }
                    setFormData(f => ({
                      ...f,
                      customerId: data.id,
                      customerName: data.name,
                      customerPhone: data.phone,
                      customerEmail: data.email,
                    }));
                    setSelectedCustomer({
                      customerId: data.id,
                      customerName: data.name,
                      customerPhone: data.phone,
                      customerEmail: data.email,
                    });
                    setShowAddCustomerPopup(false);
                    setStep(2);
                    toast.success('לקוח חדש נוסף בהצלחה!');
                  }}
                >
                  שמור והמשך
                </button>
              </div>
            </div>
          </div>
        )}
      </PopupCard>
    );
  }

  // Step 2: Charge type selection
  if (step === 2 && !skipChargeTypeStep) {
    const showBackButton = !defaultAppointment && disableBackOnStep !== 2;
    return (
      <PopupCard title="בחר סוג חיוב" onClose={onClose} onBack={showBackButton ? () => setStep(1) : undefined} customerInfo={formData.customerName}>
        <StepIndicator step={step} />
        <div className="flex flex-col gap-4 mt-4">
          {chargeTypes.map(type => (
            <motion.button
              key={type.key}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className={`rounded-xl border px-4 py-3 text-lg font-medium ${chargeType === type.key ? 'border-indigo-500 bg-indigo-100 text-indigo-700 shadow' : 'border-gray-200 bg-white text-gray-700'}`}
              onClick={() => {
                setChargeType(type.key as any);
                if (type.key === 'appointment') {
                  setStep(2.5);
                } else if (type.key === 'product') {
                  setStep(2.6);
                } else {
                  setStep(terminals.length > 0 ? 2.8 : 3);
                }
              }}
            >
              {type.label}
            </motion.button>
          ))}
        </div>
      </PopupCard>
    );
  }

  // Step 2.5: Appointment selection (if chargeType is 'appointment' && !defaultAppointment)
  if (step === 2.5 && chargeType === 'appointment' && !defaultAppointment) {
    return (
      <PopupCard title="בחר תור לחיוב" onClose={onClose} onBack={() => setStep(2)} customerInfo={formData.customerName}>
        <StepIndicator step={step} />
        <div className="mt-3">
          {appointments.length === 0 ? (
            <div className="text-center text-gray-500">אין תורים פתוחים ללקוח זה</div>
          ) : (
            <div className="grid grid-cols-1 gap-3 max-h-[340px] overflow-y-auto px-1">
              {appointments.map(app => (
                <motion.button
                  key={app.id}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className={`flex flex-col items-start rounded-xl border px-4 py-3 transition-all duration-300 shadow-sm
                    ${selectedAppointment?.id === app.id
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-lg'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-indigo-50/60'}
                  `}
                  style={{
                    minHeight: 70,
                    boxSizing: 'border-box',
                  }}
                  onClick={() => {
                    setSelectedAppointment(app);
                    // שלוף מחיר השירות (אם יש) ועדכן את הסכום בשדה amount
                    const price =
                      typeof app.metadata?.price === 'number'
                        ? app.metadata.price
                        : typeof app.services?.price === 'number'
                        ? app.services.price
                        : typeof app.price === 'number'
                        ? app.price
                        : '';
                    setFormData(f => ({
                      ...f,
                      amount: price ? price.toString() : ''
                    }));
                    setStep(terminals.length > 0 ? 2.8 : 3);
                  }}
                >
                  <div className="flex items-center gap-2 mb-1 w-full">
                    <span className="font-semibold text-indigo-700 truncate max-w-[120px]">{app.services?.name_he || 'שירות'}</span>
                    {(() => {
                      const price =
                        typeof app.metadata?.price === 'number'
                          ? app.metadata.price
                          : typeof app.services?.price === 'number'
                          ? app.services.price
                          : typeof app.price === 'number'
                          ? app.price
                          : '';
                      return price ? (
                        <span className="font-bold text-indigo-600 text-sm bg-indigo-100 px-2 py-0.5 rounded-lg">{price} ₪</span>
                      ) : null;
                    })()}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold
                      ${app.status === 'booked' ? 'bg-yellow-100 text-yellow-800' :
                        app.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                        app.status === 'completed' ? 'bg-green-100 text-green-800' :
                        app.status === 'no_show' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                      {app.status === 'booked' ? 'ממתין' :
                        app.status === 'confirmed' ? 'מאושר' :
                        app.status === 'completed' ? 'הושלם' :
                        app.status === 'no_show' ? 'לא הגיע' :
                        'בוטל'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Clock className="h-4 w-4 text-indigo-400" />
                    <span>
                      {new Date(app.start_time).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' })} 
                      {' '}
                      {new Date(app.start_time).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400 text-xs">
                    <User className="h-4 w-4" />
                    <span>{app.staff?.name || 'לא צוין'}</span>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </PopupCard>
    );
  }

  // Step 2.6: Product selection (if chargeType is 'product')
  if (step === 2.6 && chargeType === 'product') {
    return (
      <PopupCard title="בחר מוצר לחיוב" onClose={onClose} onBack={() => setStep(2)} customerInfo={formData.customerName}>
        <StepIndicator step={step} />
        <div className="space-y-4 mt-3">
          {products.length === 0 ? (
            <div className="text-center text-gray-500">אין מוצרים זמינים לעסק זה</div>
          ) : (
            products.map(prod => (
              <motion.button
                key={prod.id}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className={`w-full text-right rounded-xl border px-4 py-3 mb-2 ${selectedProduct?.id === prod.id ? 'border-indigo-500 bg-indigo-100 text-indigo-700 shadow' : 'border-gray-200 bg-white text-gray-700'}`}
                onClick={() => {
                  setSelectedProduct(prod);
                  setProductQuantity(1);
                  setFormData(f => ({ ...f, amount: prod.price.toString() }));
                  setStep(2.7); // עבור לשלב כמות
                }}
              >
                <div className="font-medium">{prod.name}</div>
                <div className="text-sm text-gray-500">{prod.price} ₪</div>
              </motion.button>
            ))
          )}
        </div>
      </PopupCard>
    );
  }

  // שלב חדש: בחירת כמות למוצר
  if (step === 2.7 && chargeType === 'product' && selectedProduct) {
    return (
      <PopupCard title={`בחר כמות עבור "${selectedProduct.name}"`} onClose={onClose} onBack={() => setStep(2.6)} customerInfo={formData.customerName}>
        <StepIndicator step={step} />
        <div className="flex flex-col items-center gap-4 mt-4">
          <div className="text-lg font-medium">{selectedProduct.name}</div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 rounded-full bg-gray-200 text-lg font-bold"
              onClick={() => setProductQuantity(q => Math.max(1, q - 1))}
              type="button"
            >-</button>
            <span className="text-xl font-bold">{productQuantity}</span>
            <button
              className="px-3 py-1 rounded-full bg-gray-200 text-lg font-bold"
              onClick={() => setProductQuantity(q => q + 1)}
              type="button"
            >+</button>
          </div>
          <div className="text-gray-500 text-sm">
            מחיר ליחידה: {selectedProduct.price} ₪
          </div>
          <div className="text-indigo-700 font-bold text-lg">
            סה"כ: {(selectedProduct.price * productQuantity).toFixed(2)} ₪
          </div>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="w-full mt-4 bg-indigo-600 text-white rounded-xl py-2.5 font-medium hover:bg-indigo-700 transition"
            onClick={() => {
              setFormData(f => ({ ...f, amount: (selectedProduct.price * productQuantity).toString() }));
              setStep(terminals.length > 0 ? 2.8 : 3);
            }}
          >
            {terminals.length > 0 ? 'המשך לבחירת מסוף' : 'המשך לאמצעי תשלום'}
          </motion.button>
        </div>
      </PopupCard>
    );
  }

  // Step 2.8: Terminal selection (if terminals exist)
  if (step === 2.8 && terminals.length > 0) {
    const backStep = chargeType === 'appointment' ? 2.5 : chargeType === 'product' ? 2.7 : 2;
    // הסתר כפתור חזור רק אם זה השלב הראשון שבו התחלנו
    const showBackButton = step !== initialStep;
    return (
      <PopupCard title="בחר מסוף לחיוב" onClose={onClose} onBack={showBackButton ? () => setStep(backStep) : undefined} customerInfo={formData.customerName}>
        <StepIndicator step={step} />
        <div className="mt-3">
          <p className="text-sm text-gray-600 text-center mb-4">
            בחר את המסוף שדרכו תרצה לבצע את החיוב
          </p>
          <div className="space-y-3 max-h-[400px] overflow-y-auto px-1">
            {terminals.map(terminal => (
              <motion.button
                key={terminal.id}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className={`w-full text-right rounded-xl border-2 px-4 py-3 transition-all duration-300 shadow-sm
                  ${selectedTerminal?.id === terminal.id
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-lg'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-indigo-50/60'}
                `}
                onClick={() => {
                  setSelectedTerminal(terminal);
                  setStep(3);
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-bold text-[17px] mb-1">
                      {terminal.terminal_name || 'מסוף ללא שם'}
                    </div>
                    <div className="text-[13px] text-gray-400">
                      {terminal.terminal_request?.form_data?.regId && (
                        <div className="mb-1">ח"פ: {terminal.terminal_request.form_data.regId}</div>
                      )}
                      <div>מספר מסוף: {terminal.pelecard_terminal_number || 'לא הוגדר'}</div>
                    </div>
                  </div>
                  {selectedTerminal?.id === terminal.id && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center"
                    >
                      <Check className="h-5 w-5 text-white" />
                    </motion.div>
                  )}
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </PopupCard>
    );
  }

  // Step 3: Payment method
  if (step === 3) {
    const backStep = terminals.length > 0 ? 2.8 : (chargeType === 'appointment' ? 2.5 : chargeType === 'product' ? 2.7 : 2);
    // הסתר כפתור חזור רק אם זה השלב הראשון שבו התחלנו
    const showBackButton = step !== initialStep;
    return (
      <PopupCard title="בחר אמצעי תשלום" onClose={onClose} onBack={showBackButton ? () => setStep(backStep) : undefined} terminalInfo={getTerminalInfo()} customerInfo={formData.customerName}>
        <StepIndicator step={step} />
        <div className="space-y-3 mt-4" dir="rtl">
          {paymentMethods.map(pm => (
            <motion.button
              key={pm.key}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`relative w-full flex items-center gap-4 rounded-2xl border-2 p-4 transition-all duration-300
                ${formData.paymentMethod === pm.key
                  ? `${pm.selectedBorder} ${pm.selectedBg} shadow-lg`
                  : `${pm.borderColor} bg-white hover:shadow-md`}
              `}
              onClick={() => {
                setFormData(f => ({ ...f, paymentMethod: pm.key }));
                setTimeout(() => setStep(4), 300);
              }}
            >
              <div className={`flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br ${pm.bgColor} flex items-center justify-center`}>
                {pm.icon}
              </div>
              <div className="flex-1 text-right">
                <span className="text-base font-semibold text-gray-800">{pm.label}</span>
              </div>
              {formData.paymentMethod === pm.key && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center"
                >
                  <Check className="h-5 w-5 text-white" />
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
      </PopupCard>
    );
  }

  // Step 4: Tip selection
  if (step === 4) {
    return (
      <PopupCard title="הוסף טיפ (אופציונלי)" onClose={onClose} onBack={() => setStep(3)} terminalInfo={getTerminalInfo()} customerInfo={formData.customerName}>
        <StepIndicator step={step} />
        <div className="space-y-4 mt-4">
          <p className="text-sm text-gray-600 text-center flex items-center justify-center gap-2">
            <Coins className="h-5 w-5 text-emerald-500" />
            <span>בחר אחוז טיפ או הזן סכום מותאם אישית</span>
          </p>
          <div className="flex gap-2">
            {tipOptions.map(percent => (
              <motion.button
                key={percent}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`flex-1 rounded-xl border-2 px-4 py-3 font-semibold transition-all duration-200
                  ${formData.tipPercent === percent
                    ? 'border-emerald-500 bg-emerald-100 text-emerald-700 shadow-lg'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-emerald-300'}
                `}
                onClick={() => setFormData(f => ({ ...f, tipPercent: percent, tipAmount: '' }))}
              >
                <div className="flex flex-col items-center gap-1">
                  <Coins className="h-5 w-5" />
                  <span className="text-lg">{percent}%</span>
                </div>
              </motion.button>
            ))}
          </div>
          <div className="relative">
            <Banknote className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="number"
              min={0}
              placeholder="או הזן סכום בשקלים"
              className="w-full rounded-xl border-2 border-gray-200 pr-11 pl-4 py-3 text-center focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all"
              value={formData.tipAmount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(f => ({ ...f, tipAmount: e.target.value, tipPercent: null }))}
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 bg-gray-100 text-gray-700 rounded-xl py-3 font-medium hover:bg-gray-200 transition flex items-center justify-center gap-2"
            onClick={() => {
              setFormData(f => ({ ...f, tipPercent: null, tipAmount: '' }));
              setStep(5);
            }}
          >
            דלג על טיפ
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 bg-indigo-600 text-white rounded-xl py-3 font-medium hover:bg-indigo-700 transition flex items-center justify-center gap-2"
            onClick={() => setStep(5)}
          >
            <Banknote className="h-5 w-5" />
            המשך
          </motion.button>
        </div>
      </PopupCard>
    );
  }

  // Step 5: Amount and installments
  if (step === 5) {
    // שליפת מחיר נכון לתור קיים (אם chargeType === 'appointment')
    let appointmentPrice = formData.amount;
    if (
      chargeType === 'appointment' &&
      defaultAppointment &&
      (!appointmentPrice || appointmentPrice === '0')
    ) {
      appointmentPrice =
        typeof defaultAppointment.metadata?.price === 'number'
          ? defaultAppointment.metadata.price.toString()
          : typeof defaultAppointment.services?.price === 'number'
          ? defaultAppointment.services.price.toString()
          : typeof defaultAppointment.price === 'number'
          ? defaultAppointment.price.toString()
          : '';
    }
    return (
      <PopupCard title="סכום לחיוב" onClose={onClose} onBack={() => setStep(4)} terminalInfo={getTerminalInfo()} customerInfo={formData.customerName}>
        <StepIndicator step={step} />
        <div className="space-y-4 mt-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Banknote className="h-4 w-4 text-indigo-500" />
              סכום (₪)
            </label>
            <input
              type="text"
              inputMode="decimal"
              className="w-full border border-gray-300 rounded-xl p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-center text-lg font-semibold"
              value={formData.amount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                  setFormData(prev => ({ ...prev, amount: value }));
                }
              }}
              placeholder="0"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <CreditCard className="h-4 w-4 text-indigo-500" />
              מספר תשלומים
            </label>
            <input
              type="number"
              min={1}
              max={12}
              className="w-full border border-gray-300 rounded-xl p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={formData.installments}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(f => ({ ...f, installments: e.target.value }))}
              required
            />
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full mt-5 bg-indigo-600 text-white rounded-xl py-2.5 font-medium hover:bg-indigo-700 transition flex items-center justify-center gap-2"
          onClick={() => setStep(6)}
        >
          <CheckCircle className="h-5 w-5" />
          לסיכום ואישור
        </motion.button>
      </PopupCard>
    );
  }

  // Step 6: Summary and confirm
  return (
    <PopupCard title="סיכום ואישור" onClose={onClose} onBack={() => { if (!loading) setStep(5); }} terminalInfo={getTerminalInfo()} customerInfo={formData.customerName}>
      <StepIndicator step={step} />
      <div className="space-y-3 mt-3">
        <div className="p-3 bg-gradient-to-br from-gray-50 to-indigo-50 rounded-xl shadow">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
              <span className="text-sm font-medium text-indigo-600">
                {formData.customerName.charAt(0)}
              </span>
            </div>
            <span className="font-medium flex items-center gap-1">
              <User className="h-4 w-4 text-indigo-500" />
              {formData.customerName}
            </span>
          </div>
          <p className="text-sm text-gray-500 mr-10 flex items-center gap-1">
            <Smartphone className="h-4 w-4 text-gray-400" />
            {formData.customerPhone}
          </p>
        </div>
        
        <div className="space-y-2 p-3 bg-gradient-to-br from-gray-50 to-purple-50 rounded-xl shadow">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <CreditCard className="h-4 w-4 text-indigo-400" />
              אמצעי תשלום:
            </span>
            <span className="font-medium">{paymentMethods.find(pm => pm.key === formData.paymentMethod)?.label}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Banknote className="h-4 w-4 text-indigo-400" />
              סכום לחיוב:
            </span>
            <span className="font-medium">{formData.amount} ₪</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Coins className="h-4 w-4 text-emerald-400" />
              טיפ:
            </span>
            <span className="font-medium">{formData.tipPercent ? `${formData.tipPercent}%` : formData.tipAmount ? `${formData.tipAmount} ₪` : 'ללא'}</span>
          </div>
          {(formData.tipPercent || formData.tipAmount) && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <Coins className="h-4 w-4 text-emerald-400" />
                סכום טיפ:
              </span>
              <span className="font-medium">
                {formData.tipPercent 
                  ? `${(parseFloat(formData.amount || '0') * (formData.tipPercent / 100)).toFixed(2)} ₪`
                  : `${formData.tipAmount} ₪`}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <CreditCard className="h-4 w-4 text-indigo-400" />
              מספר תשלומים:
            </span>
            <span className="font-medium">{formData.installments}</span>
          </div>
          <div className="border-t border-gray-200 mt-2 pt-2">
            <div className="flex justify-between items-center font-bold">
              <span className="text-gray-800 flex items-center gap-1">
                <Banknote className="h-5 w-5 text-indigo-700" />
                סה"כ לתשלום:
              </span>
              <span className="text-indigo-700">
                {(() => {
                  const baseAmount = parseFloat(formData.amount || '0');
                  let tipAmount = 0;
                  if (formData.tipPercent) {
                    tipAmount = baseAmount * (formData.tipPercent / 100);
                  } else if (formData.tipAmount) {
                    tipAmount = parseFloat(formData.tipAmount);
                  }
                  return `${(baseAmount + tipAmount).toFixed(2)} ₪`;
                })()}
              </span>
            </div>
          </div>
        </div>
        {/* הצג קישור לחשבונית אם קיים */}
        {invoiceUrl && (
          <div className="mt-3 flex justify-center">
            <a
              href={invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-bold"
            >
              הצג חשבונית
            </a>
          </div>
        )}
      </div>
      <motion.button
        whileHover={{ scale: loading ? 1 : 1.02 }}
        whileTap={{ scale: loading ? 1 : 0.98 }}
        className={`w-full mt-5 ${loading ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'} text-white rounded-xl py-2.5 font-medium transition flex items-center justify-center gap-2`}
        onClick={async () => {
          if (loading) return;
          setLoading(true);
          try {
            const { data } = await supabase.auth.getSession();
            const accessToken = data?.session?.access_token;
            if (!accessToken) {
              toast.error('יש להתחבר למערכת כדי לבצע חיוב');
              setLoading(false);
              return;
            }
            let missingFields = [];
            if (!businessId || businessId.trim() === '') {
              toast.error('מזהה העסק חסר. אנא התחבר מחדש למערכת.');
              setLoading(false);
              return;
            }
            if (!formData.customerId) missingFields.push('מזהה לקוח');
            if (!formData.customerName) missingFields.push('שם לקוח');
            if (!formData.paymentMethod) missingFields.push('אמצעי תשלום');
            if (!formData.amount) missingFields.push('סכום לחיוב');
            if (missingFields.length > 0) {
              toast.error(`נא למלא את השדות הבאים: ${missingFields.join(', ')}`);
              setLoading(false);
              return;
            }
            let tipAmount = 0;
            if (formData.tipPercent) {
              tipAmount = parseFloat(formData.amount) * (formData.tipPercent / 100);
            } else if (formData.tipAmount) {
              tipAmount = parseFloat(formData.tipAmount);
            }
            // אפשרות: להוסיף timeout
            const controller = new AbortController();
            console.log("🔍 ChargeCustomerFlow - selectedTerminal:", selectedTerminal);
            console.log("🔍 terminalId שנשלח:", selectedTerminal?.id);

            // בנה תיאור דינמי לפי סוג החיוב
            let serviceDescription = 'חיוב כללי';
            if (chargeType === 'appointment') {
              serviceDescription = selectedAppointment?.services?.name_he || defaultAppointment?.services?.name_he || 'שירות';
            } else if (chargeType === 'product' && selectedProduct) {
              serviceDescription = `${selectedProduct.name} - כמות: ${productQuantity}`;
            }

            const timeout = setTimeout(() => controller.abort(), 15000); // 15 שניות
            const res = await chargeCustomer({
              businessId: businessId,
              customerId: formData.customerId,
              amount: parseFloat(formData.amount),
              tipAmount,
              paymentMethod: formData.paymentMethod as any,
              serviceDescription,
              customerName: formData.customerName,
              customerEmail: formData.customerEmail || '',
              customerPhone: formData.customerPhone || undefined,
              customerAddress: '',
              accessToken,
              appointmentId: appointmentId || selectedAppointment?.id || undefined,
              terminalId: selectedTerminal?.id || undefined,
            });
            clearTimeout(timeout);

            // אם מדובר בחיוב על תור, עדכן סטטוס ל-completed
            if (chargeType === 'appointment' && (appointmentId || selectedAppointment?.id)) {
              await supabase
                .from('appointments')
                .update({ status: 'completed' })
                .eq('id', appointmentId || selectedAppointment?.id);
            }

            const invoiceUrl = res?.invoiceLink || res?.raw?.document_link;
            if (invoiceUrl) {
              toast.success(
                <span>
                  החיוב בוצע בהצלחה!
                  <br />
                  <a
                    href={invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      marginTop: 8,
                      padding: '8px 16px',
                      background: '#6366f1',
                      color: '#fff',
                      borderRadius: 8,
                      textDecoration: 'none',
                      fontWeight: 500,
                    }}
                  >
                    הצג חשבונית
                  </a>
                </span>
              );
            } else {
              toast.success('החיוב בוצע בהצלחה!');
            }
            setLoading(false);
            onSuccess?.();
            onClose();
          } catch (error: any) {
            setLoading(false);
            toast.error(error.message || 'אירעה שגיאה במהלך החיוב');
          }
        }}
      >
        {loading ? (
          <>
            <Loader className="h-5 w-5 animate-spin" />
            טוען...
          </>
        ) : (
          <>
            <CheckCircle className="h-5 w-5" />
            אשר וחתום
          </>
        )}
      </motion.button>
    </PopupCard>
  );
}

function PopupCard({ title, onClose, onBack, children, terminalInfo, customerInfo }: any) {
  return (
    <BottomSheet isOpen={true} onClose={onClose} ariaLabel={title}>
      <div className="flex flex-col h-full overflow-hidden">
        <div className="px-5 pt-3 pb-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {onBack && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onBack}
                  className="p-2 rounded-full hover:bg-gray-100 transition"
                  title="חזור"
                >
                  <ChevronRight className="h-5 w-5 text-gray-600" />
                </motion.button>
              )}
            </div>
            <div className="flex-1 text-center">
              <h3 className="text-xl font-bold text-gray-800">{title}</h3>
              {terminalInfo && (
                <p className="text-xs text-gray-500 mt-1">
                  מסוף: {terminalInfo.name} | ח"פ: {terminalInfo.regId}
                </p>
              )}
              {customerInfo && (
                <p className="text-xs text-indigo-600 font-medium mt-1 flex items-center justify-center gap-1">
                  <User className="h-3 w-3" />
                  לקוח: {customerInfo}
                </p>
              )}
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="p-2 rounded-full hover:bg-red-50 transition"
              title="סגור"
            >
              <X className="h-5 w-5 text-gray-500 hover:text-red-600" />
            </motion.button>
          </div>
        </div>
        <div className="px-5 pb-5 flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </BottomSheet>
  );
}